import { useQuery } from '@tanstack/react-query';

import { cameraKeys, camerasApi, type PresenceAccess } from '@/api/cameras';
import { useAuth } from '@/hooks/useAuth';

export const CAMERAS_PATH = '/webadmin/app/cameras';

/**
 * Pages a camera grant opens: the overview and a store's live video and recordings.
 * The zone editor (`/cameras/:store/zones`) stays administrator-only.
 */
export function isCameraViewerPath(pathname: string): boolean {
  if (pathname === CAMERAS_PATH || pathname === `${CAMERAS_PATH}/`) return true;
  if (!pathname.startsWith(`${CAMERAS_PATH}/`)) return false;
  const rest = pathname.slice(CAMERAS_PATH.length + 1).replace(/\/$/, '');
  return rest !== '' && !rest.includes('/');
}

/**
 * Store camera access: administrators have everything; another user needs a grant (MD-0086
 * addendum 2026-09-28). The grant opens live video and recordings; its `presence` level
 * adds the presence reports (`view`) and episode review (`review`). The zone editor stays
 * with administrators.
 */
export function useCameraAccess(): {
  canView: boolean;
  presence: PresenceAccess;
  isLoading: boolean;
} {
  const { user, status } = useAuth({ skipInitialCheck: true });
  const isAdmin = user?.role === 'admin';

  const query = useQuery({
    queryKey: cameraKeys.access(),
    queryFn: () => camerasApi.access(),
    enabled: status === 'authenticated' && !isAdmin,
    staleTime: 60_000,
  });

  if (isAdmin) return { canView: true, presence: 'review', isLoading: false };
  return {
    canView: query.data?.can_view === true,
    presence: query.data?.presence ?? 'none',
    isLoading: status === 'authenticated' && query.isLoading,
  };
}
